import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Container, Card, Form, Spinner, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAsterisk } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import FormFieldError from 'src/components/general/FormFieldError';
import { tryGetListUserProfile, tryGetListUserXApplicationXProfile, tryPutUser, tryGetUserList, tryPutUserXApplicationXProfile } from 'src/redux/user/userActionCreator';
import { clearUserList, clearProfileToEdit } from 'src/redux/user/userActions';
import { getUserToEdit, getUserProfileListData, getUserProfileListIsFetching, getUserXApplicationXProfileByUserId, getUserProfileListByUserId, getUserEditIsFetching, getListUsersXApplicationXProfileIsFetching } from 'src/redux/user/userReducer';
import { USER_LIST } from 'src/utils/constants';
import { setAssignProfiles } from 'src/utils/usersProfiles';
import { userNameData, joinLastNameName } from 'src/utils/utils';
import { tryAddAllUsersAdministrativeService, tryRemoveAllUsersAdministrativeService, tryListAllAdministrativeServices, tryListAllServiceAdministrativeByUser, tryAddNewUserAdministrativeService } from 'src/redux/administrativeService/administrativeServiceActionCreator';
import { getAdministrativeServiceListAllData, getAdministrativeServiceListAllIsFetching, getListAllServiceAdministrativeByUserData } from 'src/redux/administrativeService/administrativeServiceReducer';
import { clearListAllServiceAdministrativeByUser } from 'src/redux/administrativeService/administrativeServiceActions'
import { ToastContainer } from 'react-toastify';
import { showSuccess } from 'src/redux/globalData/globalDataActionCreator';
import { createValidationRules, isOrganismSelectionRequired, isServiceSelectionRequired } from './utils/UserValidations';
import ServiceMultiSelect from './utils/ServiceMultiSelect';
import { useJurisdictions } from './utils/useJurisdictions';
import OrganismTreeSelect from './utils/OrganismTreeSelect';
import { getAccessToken } from 'src/redux/login/loginReducer';
import { deleteOrganismsByUser, postUserOrganisms } from 'src/services/userServices';
import AppLoading from '../../common/AppLoading';

const UserEditPage = () => {
	const dispatch = useDispatch();
	const {
		register,
		handleSubmit,
		errors,
		getValues,
		setValue,
		watch
	} = useForm();
	const watchLastName = watch('lastName');
	const watchName = watch('name');
	const watchEmail = watch('email');
	const watchProfileId = watch('profileId');

	const {
		jurisdictionData,
		isLoading: jurisdictionsLoading,
		fetchJurisdictions,
		processSelectedOrganisms,
		userOrganisms,
		userOrganismsLoading,
		userOrganismsError,
		fetchUserOrganisms,
		resetUserOrganisms,
	} = useJurisdictions();
	const validationRules = createValidationRules();
	const accessToken = useSelector(state => getAccessToken(state));
	const [disableAfterClick, setDisableAfterClick] = useState(false);

	// Estados para controlar carga de datos
	const [servicesLoaded, setServicesLoaded] = useState(false);
	const [organismsLoaded, setOrganismsLoaded] = useState(false);
	const [profilesLoaded, setProfilesLoaded] = useState(false);

	// Estados y Opciones para Cuentas Especiales
	const [specialAccountsFilter, setSpecialAccountsFilter] = useState('0');
	const SPECIAL_ACCOUNTS_OPTIONS = [
		{ value: '1', label: 'Sin cuentas especiales' },
		{ value: '2', label: 'Solo cuentas especiales' },
		{ value: '3', label: 'Incluir cuentas especiales' }
	];

	//Services Administrative
	const administrativeService = useSelector(state => getAdministrativeServiceListAllData(state))?.records?.sort((a, b) => a.code > b.code ? 1 : -1);
	const administrativeServiceIsFetching = useSelector(state => getAdministrativeServiceListAllIsFetching(state));

	// User to edit data
	const userToEdit = useSelector(state => getUserToEdit(state));
	const userProfile = useSelector(state => getUserXApplicationXProfileByUserId(state, userToEdit?.id));
	const userEditIsFetching = useSelector(state => getUserEditIsFetching(state));

	// List profiles
	const listProfileData = useSelector(state => getUserProfileListData(state));
	const listProfileIsFetching = useSelector(state => getUserProfileListIsFetching(state));
	const listProfileDataIdUser = useSelector(state => getUserProfileListByUserId(state, userProfile?.profileId));
	const valuelistProfileDataIdUser = listProfileDataIdUser?.id;
	const profileNameArray = setAssignProfiles();
	const profileNameArrayRecords = listProfileData?.filter(item => profileNameArray?.includes(item?.name));
	const profileNameArrayRecordsFilter = profileNameArrayRecords?.filter(value => {
		return !listProfileData?.find(value2 => value.id == valuelistProfileDataIdUser);
	})?.sort((a, b) => a.name > b.name ? 1 : -1);

	// Find List Users Asignated
	const valueUserProfile = listProfileDataIdUser?.name ? listProfileDataIdUser?.name : undefined;
	const [selectedProfile, setSelectedProfile] = useState(valueUserProfile);
	const requiresServices = isServiceSelectionRequired(selectedProfile);
	const requiresOrganisms = isOrganismSelectionRequired(selectedProfile);

	const onFormSubmit = async (data) => {
		try {
			setDisableAfterClick(true);

			// PASO 1: Actualizar datos básicos del usuario
			const userXApplicationXProfile = userProfile;
			let parseProfileId = Number.isInteger(parseInt(data?.profileId));
			const fundProfileId = listProfileData?.find(item => item?.name == data?.profileId);
			userXApplicationXProfile.profileId = parseProfileId ? data?.profileId : fundProfileId?.id;

			const userData = {
				id: userToEdit?.id,
				name: joinLastNameName(data?.lastName, data?.name).toUpperCase(),
				alias: data?.name?.toUpperCase(),
				email: data?.email,
				valid: userToEdit?.valid,
				userXApplicationXProfile
			};

			const userResponse = await dispatch(tryPutUser(userData));
			if (userResponse?.status !== 200) {
				console.error('Error actualizando usuario:', userResponse);
				setDisableAfterClick(false);
				return;
			}

			// PASO 1.1: Actualizar perfil 
			const profileResponse = await dispatch(tryPutUserXApplicationXProfile(userData.userXApplicationXProfile));
			if (profileResponse?.status !== 200) {
				console.error('Error actualizando perfil del usuario:', profileResponse);
				setDisableAfterClick(false);
				return;
			};

			const userId = userResponse?.data?.id;
			const userIdArray = [userId];

			// PASO 2: Actualizar servicios administrativos
			if (requiresServices) {
				const removeResponse = await dispatch(tryRemoveAllUsersAdministrativeService(userId));
				if (removeResponse?.status === 200) {
					const selectedAdministrativeService = selectedService?.map(item => item.value);
					await dispatch(tryAddNewUserAdministrativeService(userIdArray, selectedAdministrativeService));
				}
			} else {
				await dispatch(tryAddAllUsersAdministrativeService(userId));
			}

			// PASO 3: Actualizar organismos
			if (requiresOrganisms) {
				// 3.1: Validar que hay organismos seleccionados
				const selectedValues = selectedOrganisms || [];
				if (selectedValues.length === 0) {
					console.warn('⚠️ No se seleccionaron organismos');
					setDisableAfterClick(false);
					return;
				}

				// 3.2: Validar cuenta especial
				if (specialAccountsFilter === '0' || !specialAccountsFilter) {
					console.warn('⚠️ Cuenta especial no seleccionada:', specialAccountsFilter);
					setDisableAfterClick(false);
					return;
				}

				// ✅ 3.3: CORREGIR - Procesar IDs de organismos
				let organismIds = [];
				try {
					// Si selectedValues es un array de objetos {value: "organism-123", title: "...", key: "..."}
					if (selectedValues[0]?.value) {
						organismIds = selectedValues.map(item => {
							const value = item.value || item;
							// Extraer el ID numérico
							if (typeof value === 'string') {
								if (value.startsWith('organism-')) {
									return parseInt(value.replace('organism-', ''), 10);
								} else if (value.startsWith('jurisdiction-')) {
									// Ignorar jurisdicciones
									return null;
								} else {
									return parseInt(value, 10);
								}
							}
							return parseInt(value, 10);
						}).filter(id => id !== null && !isNaN(id));
					}
					// Si es un array de strings directamente
					else if (typeof selectedValues[0] === 'string') {
						organismIds = processSelectedOrganisms(selectedValues);
					}
					// Si ya son números
					else if (typeof selectedValues[0] === 'number') {
						organismIds = selectedValues;
					}
				} catch (processError) {
					console.error('❌ Error procesando organismos:', processError);
					setDisableAfterClick(false);
					return;
				}

				if (organismIds.length === 0) {
					console.warn('⚠️ No se pudieron procesar los IDs de organismos');
					setDisableAfterClick(false);
					return;
				}

				// 3.4: Eliminar organismos existentes
				if (userOrganisms && userOrganisms.length > 0) {
					await deleteOrganismsByUser(accessToken, userId);
				}

				// 3.5: Agregar los nuevos organismos    
				const organismResponse = await postUserOrganisms(
					accessToken,
					userId,
					organismIds,
					specialAccountsFilter
				);
				if (organismResponse?.error ||
					(organismResponse?.status !== 200 && organismResponse?.status !== 201)) {
					console.error('❌ Error asignando organismos:', organismResponse);
					setDisableAfterClick(false);
					return;
				}
			} else {
				// Si el perfil NO requiere organismos, eliminar los existentes
				if (userOrganisms && userOrganisms.length > 0) {
					await deleteOrganismsByUser(accessToken, userId);
				}
			}

			// PASO 4: Éxito - Redirigir
			dispatch(clearListAllServiceAdministrativeByUser());
			showSuccess('Usuario editado correctamente.');
			dispatch(push(USER_LIST));
		} catch (error) {
			console.error('❌ Error general:', error);
		} finally {
			setDisableAfterClick(false);
		}
	};

	const onChangeProfile = item => {
		setSelectedProfile(item.target.options[item.target.selectedIndex].text);
		setSelectedService([]);
		setSelectedOrganisms([]);
	}

	// SERVICE MULTISELECT
	// SERVICE MULTISELECT 1: Generar opciones de servicios con useMemo
	const [selectedService, setSelectedService] = useState([]);
	const serviceOptions = useMemo(() => {
		if (!administrativeService?.length) return [];
		return administrativeService.map(service => ({
			label: `${service?.code} - ${service?.shortName}`,
			value: service?.id
		}));
	}, [administrativeService]);
	// SERVICE MULTISELECT 2: Servicios actuales del usuario
	const userCurrentServices = useSelector(state => getListAllServiceAdministrativeByUserData(state));
	const userCurrentServicesOptions = useMemo(() => {
		if (!userCurrentServices?.length) return [];
		return userCurrentServices.map(item => ({
			label: `${item?.code} - ${item?.shortName}`,
			value: item?.id
		}));
	}, [userCurrentServices]);
	// SERVICE MULTISELECT 3: Cargar servicios del usuario cuando estén disponibles
	useEffect(() => {
		if (userCurrentServicesOptions.length > 0 ) {
			setSelectedService(userCurrentServicesOptions);
		}
	}, [userCurrentServicesOptions]);

	// ORGANISMS MULTISELECT
	// ORGANISMS MULTISELECT 1: Transformar organismos del usuario a formato para OrganismTreeSelect
	const [selectedOrganisms, setSelectedOrganisms] = useState([]);
	const userCurrentOrganismsOptions = useMemo(() => {
		if (!userOrganisms?.length) return [];
		return userOrganisms.map(organism => ({
			key: `organism-${organism?.id}`,
			title: `${organism?.id} - ${organism?.shortName || organism?.name}`,
			value: `organism-${organism?.id}`,
		}));
	}, [userOrganisms]);

	// List Users X Application X Profile
	const listUsersXApplicationXProfileIsFetching = useSelector(state => getListUsersXApplicationXProfileIsFetching(state));

	// Calcular estado de loading completo
	const isLoadingUserData = !userToEdit?.id || listUsersXApplicationXProfileIsFetching;
	const isLoadingProfiles = listProfileIsFetching || !profilesLoaded;
	const isLoadingServices = administrativeServiceIsFetching || !servicesLoaded;
	const isLoadingOrganisms = userOrganismsLoading || !organismsLoaded;
	const isLoading = isLoadingUserData || isLoadingProfiles || isLoadingServices || isLoadingOrganisms || jurisdictionsLoading;

	// SUBMIT BUTTON
	const isSelectedService = selectedService.length === 0;
	const isSelectedOrganisms = !selectedOrganisms || selectedOrganisms.length === 0;
	const isSubmitDisabled = useMemo(() => {
		const formValues = getValues();
		if (!formValues.lastName?.trim()) return true;
		if (!formValues.name?.trim()) return true;
		if (!formValues.email?.trim()) return true;
		if (!formValues.profileId) return true;

		// Para perfiles específicos: validar que requieren servicios
		if (requiresServices) {
			if (isSelectedService) return true;
		}

		// Para "Consulta por Organismo": validar organismos también
		if (requiresOrganisms) {
			if (isSelectedOrganisms) return true;
		}

		return false;
	}, [
		// userCreateIsFetching,
		selectedProfile,
		requiresServices,
		isSelectedService,
		isSelectedOrganisms,
		watchLastName,
		watchName,
		watchEmail,
		watchProfileId
	]);

	// Sincronizar datos del usuario cuando terminan de cargar
	useEffect(() => {
		if (userToEdit) {
			const { lastName, firstName } = userNameData(userToEdit?.name);
			if (lastName && !getValues('lastName')) setValue('lastName', lastName);
			if (firstName && !getValues('name')) setValue('name', firstName);
			if (userToEdit.email && !getValues('email')) setValue('email', userToEdit.email);
		}
	}, [userToEdit, setValue, getValues]);

	// Sincronizar el perfil seleccionado cuando los datos terminan de cargar
	useEffect(() => {
		if (valueUserProfile && !selectedProfile) {
			setSelectedProfile(valueUserProfile);
			setValue('profileId', valueUserProfile);
		}
	}, [valueUserProfile, selectedProfile, setValue]);

	// ORGANISMS MULTISELECT 2: Cargar organismos del usuario cuando estén disponibles
	useEffect(() => {
		if (userCurrentOrganismsOptions.length > 0 && selectedOrganisms.length === 0) {
			setSelectedOrganisms(userCurrentOrganismsOptions);
		}
	}, [userCurrentOrganismsOptions]);

	// ORGANISMS MULTISELECT 3: Reset cuando cambia el perfil
	useEffect(() => {
		if (selectedProfile && !isOrganismSelectionRequired(selectedProfile)) {
			setSelectedOrganisms([]);
		} else if (selectedProfile && isOrganismSelectionRequired(selectedProfile)) {
			if (selectedOrganisms.length === 0 && userCurrentOrganismsOptions.length > 0) {
				setSelectedOrganisms(userCurrentOrganismsOptions);
			}
		}
	}, [selectedProfile, userCurrentOrganismsOptions]);

	// SELECT SpecialAccountOption basado en organismos del usuario
	useEffect(() => {
		if (userOrganisms && userOrganisms.length > 0) {
			const specialAccountOption = userOrganisms[0]?.specialAccountOption;
			if (specialAccountOption !== undefined && specialAccountOption !== null) {
				setSpecialAccountsFilter(String(specialAccountOption));
			}
		}
	}, [userOrganisms]);

	useEffect(() => {
		setServicesLoaded(false);
		setOrganismsLoaded(false);
		setProfilesLoaded(false);
		dispatch(clearUserList());
		dispatch(clearProfileToEdit());
		dispatch(clearListAllServiceAdministrativeByUser());
		dispatch(tryListAllAdministrativeServices());
		dispatch(tryGetListUserXApplicationXProfile());
		dispatch(tryGetListUserProfile());
		dispatch(tryGetUserList());
		if (userToEdit?.id) {
			dispatch(tryListAllServiceAdministrativeByUser(userToEdit?.id));
			fetchUserOrganisms(userToEdit.id);
		}
		fetchJurisdictions();
	}, [userToEdit?.id, dispatch, fetchUserOrganisms, fetchJurisdictions]);

	// Marcar cuando los datos están cargados para el loading
	useEffect(() => {
		if (!listProfileIsFetching && listProfileData !== undefined) {
			setProfilesLoaded(true);
		}

		if (!administrativeServiceIsFetching && administrativeService !== undefined) {
			setServicesLoaded(true);
		}

		if (!userOrganismsLoading && (userOrganisms !== null || userOrganismsError !== null)) {
			setOrganismsLoaded(true);
		}
	}, [
		listProfileIsFetching,
		listProfileData,
		administrativeServiceIsFetching,
		administrativeService,
		userOrganismsLoading,
		userOrganisms,
		userOrganismsError
	]);

	return (
		<Container fluid className='mb-5'>
			<ToastContainer />
			<Card className='mb-3'>
				<Card.Header>
					<h6 className='mb-0'>Editar usuario</h6>
				</Card.Header>
				<Card.Body >
					<span className='mb-3 text-danger d-flex mandatory-label justify-content-end font-weight-bold font-italic align-items-center'>
						<FontAwesomeIcon icon={faAsterisk} className="mr-2 text-danger mandatory-asterisk" />
						Obligatorio
					</span>

					<Form onSubmit={handleSubmit(onFormSubmit)}>
						<Form.Group as={Row}>
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm={4}>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Apellido:
							</Form.Label>
							<Col sm={4}>
								<Form.Control
									ref={register(validationRules.lastName)}
									type='text'
									placeholder="Apellido"
									name='lastName'
									id='lastName'
									defaultValue={userNameData(userToEdit?.name)?.lastName}
									disabled={userEditIsFetching}
									maxLength={50}
									minLength={3}
								/>
								<FormFieldError errors={errors?.lastName} />
							</Col>
						</Form.Group>

						<Form.Group as={Row}>
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm={4}>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Nombre:
							</Form.Label>
							<Col sm={4}>
								<Form.Control
									ref={register(validationRules.name)}
									type='text'
									placeholder="Nombre"
									name='name'
									id='name'
									defaultValue={userNameData(userToEdit?.name)?.firstName}
									disabled={userEditIsFetching}
									maxLength={50}
									minLength={3}
								/>
								<FormFieldError errors={errors?.name} />
							</Col>
						</Form.Group>

						<Form.Group as={Row}>
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm={4}>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Correo electrónico:
							</Form.Label>
							<Col sm={4}>
								<Form.Control
									ref={register(validationRules.email)}
									type='email'
									placeholder="Correro electrónico"
									name='email'
									id='email'
									defaultValue={`${userToEdit?.email}`}
									readOnly={true}
								/>
								<FormFieldError errors={errors?.email} />
							</Col>
						</Form.Group>

						<Form.Group as={Row}>
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm={4}>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Grupo:
							</Form.Label>
							<Col sm={4}>
								<Form.Control
									as='select'
									name='profileId'
									id='profileId'
									ref={register}
									defaultValue={valueUserProfile}
									disabled={userEditIsFetching}
									onChange={event => onChangeProfile(event)}
								>
									<option className='text-black-color' value={valueUserProfile} disabled={userEditIsFetching} >
										{`${listProfileDataIdUser?.name || ''}`}
									</option>
									{!userEditIsFetching &&
										profileNameArrayRecordsFilter?.map((item, index) =>
											<option key={index} className='text-black-color' value={item.id}>
												{`${item.name || ''}`}
											</option>
										)}
								</Form.Control>
							</Col>
							{listProfileIsFetching &&
								<Spinner className='spinner-border text-danger' animation='border' size='sm' />
							}
						</Form.Group>

						{requiresServices &&
							<ServiceMultiSelect
								options={serviceOptions}
								value={selectedService}
								setSelectedService={setSelectedService}
								isLoading={administrativeServiceIsFetching}
								required={true}
								showLabel={true}
								showValidationError={selectedService.length === 0}
							/>
						}

						{requiresOrganisms &&
							<>
								{/* MultiSelect de Organismos */}
								<OrganismTreeSelect
									treeData={jurisdictionData}
									value={selectedOrganisms}
									setSelectedOrganisms={setSelectedOrganisms}
									loading={jurisdictionsLoading}
									placeholder="Seleccione organismos"
								/>

								{/* Select de Cuentas Especiales */}
								<Form.Group as={Row}>
									<Form.Label className='text-right text-black-color' column sm={4}>
										<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
										Cuentas especiales:
									</Form.Label>
									<Col sm={4}>
										<Form.Control
											as='select'
											value={specialAccountsFilter}
											onChange={(e) => setSpecialAccountsFilter(e.target.value)}
											className='text-black-color'
										>
											{SPECIAL_ACCOUNTS_OPTIONS.map(option => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</Form.Control>
									</Col>
								</Form.Group>
							</>
						}

						<div className='d-flex justify-content-around mt-4 mb-3'>
							<Button
								variant='primary'
								size='lg'
								disabled={userEditIsFetching || disableAfterClick}
								onClick={() => dispatch(push(USER_LIST))}
							>
								Cancelar
							</Button>
							<span className={(userEditIsFetching ? '' : 'hidden')}>
								<Spinner className='spinner-border text-danger' animation='border' />
							</span>
							<Button
								type='submit'
								variant='success'
								size='lg'
								disabled={isSubmitDisabled || disableAfterClick}
							>
								{disableAfterClick ? 'Guardando...' : 'Guardar'}
							</Button>
						</div>
					</Form>
				</Card.Body>
			</Card>
			<AppLoading isLoading={isLoading} />
		</Container>
	)
};

export default UserEditPage;